import { supabase } from './supabase';

// Why (RLS): every tenant-scoped table requires created_by + tenant_id in the
// INSERT payload. dsr_reports.dsr_insert enforces created_by = auth.uid() at
// the policy layer; section tables only check tenant_id today but we still
// always populate created_by for forward-compatibility.
async function getAuthUserId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('Not authenticated');
  return session.user.id;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type ShiftType = 'day' | 'night' | 'full';
export type DSRStatus = 'draft' | 'submitted' | 'reviewed' | 'flagged' | 'amended' | 'approved';
export type DSRVariant = 'lite' | 'standard' | 'full';
export type ActivityRiskLevel = 'low' | 'medium' | 'high';
export type ActivityStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled' | 'deferred';
export type EquipmentStatus = 'active' | 'standby' | 'down' | 'removed';

export interface DSREntryReport {
  id: string;
  tenant_id: string;
  project_id: string;
  report_date: string;
  shift_type: ShiftType;
  status: DSRStatus;
  variant: DSRVariant;
  submitted_at: string | null;
  created_by: string;
  created_at: string;
  projects?: {
    name: string;
    code: string | null;
    operator_name: string | null;
    rig_name: string | null;
    well_name: string | null;
    operational_phase: string | null;
    water_depth_ft: number | null;
    projo_project_id: string | null;
  } | null;
}

// ─── createDSR ───────────────────────────────────────────────────────────────

export interface CreateDSRParams {
  tenantId: string;
  projectId: string;
  createdBy: string;
}

// Why: an SSR landing on /dsr/new must always reach a usable draft. If they
// already have a draft for today, return it instead of inserting a duplicate
// (the unique constraint on (project_id, report_date, shift_type) would
// otherwise reject the second insert with Postgres error 23505).
//
// Why (variant freezing — ARCH-A2): a new DSR captures projects.dsr_variant
// at creation and stores it on dsr_reports.variant for life. Returning an
// existing draft does not re-resolve the variant — the existing draft already
// has the variant it was filed under, even if the project flipped since.
export async function createDSR(params: CreateDSRParams): Promise<string> {
  const reportDate = new Date().toISOString().slice(0, 10);

  // First check for an existing DSR by this user for today.
  const { data: existing, error: existingErr } = await supabase
    .from('dsr_reports')
    .select('id')
    .eq('project_id', params.projectId)
    .eq('created_by', params.createdBy)
    .eq('report_date', reportDate)
    .eq('is_deleted', false)
    .maybeSingle();

  if (existingErr) throw new Error(existingErr.message);
  // Variant is frozen per ARCH-A2; do not overwrite from project on re-fetch.
  if (existing?.id) return existing.id;

  // Resolve the project's current variant for the new DSR. Default 'standard'
  // covers the case where the project lookup returns no row (RLS-filtered or
  // stale id) — keeps creation robust rather than blocking the SSR.
  const { data: project } = await supabase
    .from('projects')
    .select('dsr_variant')
    .eq('id', params.projectId)
    .maybeSingle();

  const variant: DSRVariant = (project?.dsr_variant as DSRVariant | undefined) ?? 'standard';

  // Insert a new draft. shift_type defaults to 'full' (NOT NULL); user can
  // change it from the Daily Setup form.
  const { data, error } = await supabase
    .from('dsr_reports')
    .insert({
      tenant_id: params.tenantId,
      project_id: params.projectId,
      created_by: params.createdBy,
      report_date: reportDate,
      shift_type: 'full',
      status: 'draft',
      variant,
    })
    .select('id')
    .single();

  if (error) {
    // Unique violation — another row exists for the same (project, date, full).
    // Re-fetch and return its id rather than failing the user.
    if ((error as { code?: string }).code === '23505') {
      const { data: dup } = await supabase
        .from('dsr_reports')
        .select('id')
        .eq('project_id', params.projectId)
        .eq('report_date', reportDate)
        .eq('shift_type', 'full')
        .maybeSingle();
      if (dup?.id) return dup.id;
    }
    throw new Error(error.message);
  }
  return data.id;
}

// ─── DSR header (used by the shell) ──────────────────────────────────────────

export async function getDSREntryReport(dsrId: string): Promise<DSREntryReport> {
  const { data, error } = await supabase
    .from('dsr_reports')
    .select(
      `id, tenant_id, project_id, report_date, shift_type, status, variant,
       submitted_at, created_by, created_at,
       projects ( name, code, operator_name, rig_name, well_name,
                  operational_phase, water_depth_ft, projo_project_id )`
    )
    .eq('id', dsrId)
    .single();
  if (error || !data) throw new Error(error?.message ?? 'DSR not found');
  return data as unknown as DSREntryReport;
}

export async function updateDSRShift(dsrId: string, shiftType: ShiftType): Promise<void> {
  const { error } = await supabase
    .from('dsr_reports')
    .update({ shift_type: shiftType })
    .eq('id', dsrId);
  if (error) throw new Error(error.message);
}

export async function submitDSR(dsrId: string): Promise<void> {
  const { data: updated, error } = await supabase
    .from('dsr_reports')
    .update({ status: 'submitted', submitted_at: new Date().toISOString() })
    .eq('id', dsrId)
    .select('id, project_id, tenant_id')
    .single();
  if (error) throw new Error(error.message);

  // Fire-and-forget alert engine evaluation for this project. The
  // alert engine itself re-triggers risk-score for any DSR that
  // emits a new alert (see supabase/functions/alert-engine). For the
  // vast majority of submits with no new alert condition, we need
  // the separate risk-score call below so the composite score still
  // reflects the latest DSR state.
  if (updated) {
    const [{ triggerAlertEngine }, { triggerRiskScore }] = await Promise.all([
      import('./alerts'),
      import('./riskScore'),
    ]);
    void triggerAlertEngine({
      projectId: updated.project_id,
      tenantId: updated.tenant_id,
      dsrId: updated.id,
      triggeredBy: 'dsr_submit',
    });
    void triggerRiskScore({
      dsrId: updated.id,
      tenantId: updated.tenant_id,
    });
  }
}

// ─── Daily Setup ─────────────────────────────────────────────────────────────

export interface DailySetupRow {
  id: string;
  dsr_report_id: string;
  weather_condition: string | null;
  wind_speed_knots: number | null;
  visibility_nm: number | null;
  sea_state_beaufort: number | null;
  temperature_celsius: number | null;
  site_conditions_notes: string | null;
  rig_activity_codes: string[] | null;
  wind_direction: string | null;
  precipitation: string | null;
  weather_fetched_at: string | null;
  weather_source: 'auto' | 'manual' | null;
  gps_latitude: number | null;
  gps_longitude: number | null;
}

export interface SaveDailySetupParams {
  dsrReportId: string;
  tenantId: string;
  weatherCondition?: string | null;
  windSpeedKnots?: number | null;
  visibilityNm?: number | null;
  seaStateBeaufort?: number | null;
  temperatureCelsius?: number | null;
  siteConditionsNotes?: string | null;
  rigActivityCodes?: string[] | null;
  windDirection?: string | null;
  precipitation?: string | null;
  weatherFetchedAt?: string | null;
  weatherSource?: 'auto' | 'manual' | null;
  gpsLatitude?: number | null;
  gpsLongitude?: number | null;
}

const DAILY_SETUP_SELECT =
  'id, dsr_report_id, weather_condition, wind_speed_knots, visibility_nm, ' +
  'sea_state_beaufort, temperature_celsius, site_conditions_notes, rig_activity_codes, ' +
  'wind_direction, precipitation, weather_fetched_at, weather_source, gps_latitude, gps_longitude';

export async function getDailySetup(dsrId: string): Promise<DailySetupRow | null> {
  const { data, error } = await supabase
    .from('dsr_daily_setups')
    .select(DAILY_SETUP_SELECT)
    .eq('dsr_report_id', dsrId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as DailySetupRow | null;
}

export async function saveDailySetup(params: SaveDailySetupParams): Promise<void> {
  const userId = await getAuthUserId();
  const { error } = await supabase
    .from('dsr_daily_setups')
    .upsert(
      {
        dsr_report_id: params.dsrReportId,
        tenant_id: params.tenantId,
        created_by: userId,
        weather_condition: params.weatherCondition ?? null,
        wind_speed_knots: params.windSpeedKnots ?? null,
        visibility_nm: params.visibilityNm ?? null,
        sea_state_beaufort: params.seaStateBeaufort ?? null,
        temperature_celsius: params.temperatureCelsius ?? null,
        site_conditions_notes: params.siteConditionsNotes ?? null,
        rig_activity_codes: params.rigActivityCodes ?? null,
        wind_direction: params.windDirection ?? null,
        precipitation: params.precipitation ?? null,
        weather_fetched_at: params.weatherFetchedAt ?? null,
        weather_source: params.weatherSource ?? null,
        gps_latitude: params.gpsLatitude ?? null,
        gps_longitude: params.gpsLongitude ?? null,
      },
      { onConflict: 'dsr_report_id' },
    );
  if (error) throw new Error(error.message);
}

// ─── Personnel Summaries ─────────────────────────────────────────────────────

export interface PersonnelSummaryRow {
  id: string;
  dsr_report_id: string;
  company_name: string;
  crew_type: string;
  personnel_count: number;
  sse_count: number | null;
  supervisor_name: string | null;
  notes: string | null;
}

export interface PersonnelSummaryInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  companyName: string;
  crewType: string;
  personnelCount: number;
  sseCount?: number | null;
  supervisorName?: string | null;
  notes?: string | null;
}

const PERSONNEL_SELECT =
  'id, dsr_report_id, company_name, crew_type, personnel_count, sse_count, supervisor_name, notes';

export async function getPersonnelList(dsrId: string): Promise<PersonnelSummaryRow[]> {
  const { data, error } = await supabase
    .from('dsr_personnel_summaries')
    .select(PERSONNEL_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as PersonnelSummaryRow[];
}

export async function savePersonnelRow(params: PersonnelSummaryInput): Promise<PersonnelSummaryRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    company_name: params.companyName,
    crew_type: params.crewType,
    personnel_count: params.personnelCount,
    sse_count: params.sseCount ?? null,
    supervisor_name: params.supervisorName ?? null,
    notes: params.notes ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_personnel_summaries')
      .update(payload)
      .eq('id', params.id)
      .select(PERSONNEL_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as PersonnelSummaryRow;
  }
  const { data, error } = await supabase
    .from('dsr_personnel_summaries')
    .insert(payload)
    .select(PERSONNEL_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as PersonnelSummaryRow;
}

export async function deletePersonnelRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_personnel_summaries')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Lite Personnel (variant='lite' only) ────────────────────────────────────
// Lite captures POB / SSE / crew note as three columns on dsr_reports
// instead of per-crew rows in dsr_personnel_summaries. See ARCH-A1
// rationale: avoids forcing a sentinel per-crew row that would pollute
// per-company breakdowns on dashboards (KI-052-class divergence).
// On Standard/Full DSRs these columns remain NULL and the per-crew
// dsr_personnel_summaries rows are authoritative.

export interface LitePersonnelRow {
  dsr_report_id: string;
  lite_total_pob: number | null;
  lite_sse_count: number | null;
  lite_crew_note: string | null;
}

export interface SaveLitePersonnelParams {
  dsrReportId: string;
  totalPob: number;
  sseCount: number;
  crewNote?: string | null;
}

export async function getLitePersonnel(dsrId: string): Promise<LitePersonnelRow | null> {
  const { data, error } = await supabase
    .from('dsr_reports')
    .select('id, lite_total_pob, lite_sse_count, lite_crew_note')
    .eq('id', dsrId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;
  return {
    dsr_report_id: data.id as string,
    lite_total_pob: (data.lite_total_pob as number | null) ?? null,
    lite_sse_count: (data.lite_sse_count as number | null) ?? null,
    lite_crew_note: (data.lite_crew_note as string | null) ?? null,
  };
}

// Why (RLS): dsr_reports.dsr_update enforces the single-author DSR lock —
// the SSR who created the draft can update it; post-submit, the creator and
// any in-scope supervisor can update. The DB-layer CHECK constraints on
// dsr_reports (chk_lite_pob_nonneg, chk_lite_sse_nonneg, chk_lite_sse_le_pob,
// chk_lite_crew_note_len) reject malformed payloads; the Zod schema in the
// form catches them earlier with friendlier messages.
export async function saveLitePersonnel(params: SaveLitePersonnelParams): Promise<void> {
  const { error } = await supabase
    .from('dsr_reports')
    .update({
      lite_total_pob: params.totalPob,
      lite_sse_count: params.sseCount,
      lite_crew_note: params.crewNote ?? null,
    })
    .eq('id', params.dsrReportId);

  if (error) throw new Error(error.message);
}

// ─── Activities ──────────────────────────────────────────────────────────────

export interface ActivityRow {
  id: string;
  dsr_report_id: string;
  sequence_order: number;
  description: string;
  status: ActivityStatus;
  risk_level: ActivityRiskLevel;
  risk_tags: string[];
  risk_justification: string | null;
  risk_factor_category: string[];
  start_time: string | null;
  duration_minutes: number | null;
  crew_size: number | null;
  is_jsa_required: boolean;
  is_complete: boolean;
  notes: string | null;
}

export interface ActivityInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  sequenceOrder: number;
  description: string;
  status: ActivityStatus;
  riskLevel: ActivityRiskLevel;
  riskTags: string[];
  riskJustification?: string | null;
  riskFactorCategory?: string[];
  startTime?: string | null;
  durationMinutes?: number | null;
  crewSize?: number | null;
  isJsaRequired: boolean;
  isComplete: boolean;
  notes?: string | null;
}

const ACTIVITY_SELECT =
  'id, dsr_report_id, sequence_order, description, status, risk_level, ' +
  'risk_tags, risk_justification, risk_factor_category, start_time, ' +
  'duration_minutes, crew_size, is_jsa_required, is_complete, notes';

// Why: start_time is TIMESTAMPTZ in the schema but the time picker emits
// "HH:MM". Wrap the bare time in today's date so Postgres accepts the cast.
function normalizeStartTime(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.includes('T') || value.length > 10) return value;
  const today = new Date().toISOString().slice(0, 10);
  const timePart = value.length <= 5 ? `${value}:00` : value;
  return `${today}T${timePart}`;
}

export async function getActivitiesList(dsrId: string): Promise<ActivityRow[]> {
  const { data, error } = await supabase
    .from('dsr_activities')
    .select(ACTIVITY_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('sequence_order', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ActivityRow[];
}

export async function saveActivityRow(params: ActivityInput): Promise<ActivityRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    sequence_order: params.sequenceOrder,
    description: params.description,
    status: params.status,
    risk_level: params.riskLevel,
    risk_tags: params.riskTags,
    risk_justification: params.riskJustification ?? null,
    risk_factor_category: params.riskFactorCategory ?? [],
    start_time: normalizeStartTime(params.startTime),
    duration_minutes: params.durationMinutes ?? null,
    crew_size: params.crewSize ?? null,
    is_jsa_required: params.isJsaRequired,
    is_complete: params.isComplete,
    notes: params.notes ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_activities')
      .update(payload)
      .eq('id', params.id)
      .select(ACTIVITY_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as ActivityRow;
  }
  const { data, error } = await supabase
    .from('dsr_activities')
    .insert(payload)
    .select(ACTIVITY_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as ActivityRow;
}

export async function deleteActivityRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_activities')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Equipment ───────────────────────────────────────────────────────────────

export interface EquipmentRow {
  id: string;
  dsr_report_id: string;
  equipment_name: string;
  equipment_type: string | null;
  equipment_identifier: string | null;
  status: EquipmentStatus;
  hours_operated: number | null;
  operator_crew: string | null;
  notes: string | null;
}

export interface EquipmentInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  equipmentName: string;
  equipmentType?: string | null;
  equipmentIdentifier?: string | null;
  status: EquipmentStatus;
  hoursOperated?: number | null;
  operatorCrew?: string | null;
  notes?: string | null;
}

const EQUIPMENT_SELECT =
  'id, dsr_report_id, equipment_name, equipment_type, equipment_identifier, ' +
  'status, hours_operated, operator_crew, notes';

export async function getEquipmentList(dsrId: string): Promise<EquipmentRow[]> {
  const { data, error } = await supabase
    .from('dsr_equipment')
    .select(EQUIPMENT_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as EquipmentRow[];
}

export async function saveEquipmentRow(params: EquipmentInput): Promise<EquipmentRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    equipment_name: params.equipmentName,
    equipment_type: params.equipmentType ?? null,
    equipment_identifier: params.equipmentIdentifier ?? null,
    status: params.status,
    hours_operated: params.hoursOperated ?? null,
    operator_crew: params.operatorCrew ?? null,
    notes: params.notes ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_equipment')
      .update(payload)
      .eq('id', params.id)
      .select(EQUIPMENT_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as EquipmentRow;
  }
  const { data, error } = await supabase
    .from('dsr_equipment')
    .insert(payload)
    .select(EQUIPMENT_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as EquipmentRow;
}

export async function deleteEquipmentRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_equipment')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Permits ─────────────────────────────────────────────────────────────────

export type PermitType =
  | 'hot_work'
  | 'confined_space_entry'
  | 'critical_lift'
  | 'electrical_isolation'
  | 'loto'
  | 'excavation'
  | 'overwater_work'
  | 'radiation'
  | 'general_work'
  | 'other';

export type PermitStatus = 'open' | 'active' | 'suspended' | 'closed' | 'cancelled';

export interface PermitRow {
  id: string;
  dsr_report_id: string;
  permit_type: PermitType;
  permit_number: string;
  status: PermitStatus;
  issued_by: string;
  issued_at: string;
  expires_at: string | null;
  work_location: string | null;
  is_verified: boolean;
  verified_by: string | null;
  verified_at: string | null;
  activity_entry_id: string | null;
  activity_description: string | null;
  comments: string | null;
}

export interface PermitInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  permitType: PermitType;
  permitNumber: string;
  status: PermitStatus;
  issuedBy: string;
  issuedAt: string;
  expiresAt?: string | null;
  workLocation?: string | null;
  isVerified: boolean;
  activityEntryId?: string | null;
  activityDescription?: string | null;
  comments?: string | null;
}

const PERMIT_SELECT =
  'id, dsr_report_id, permit_type, permit_number, status, issued_by, issued_at, ' +
  'expires_at, work_location, is_verified, verified_by, verified_at, ' +
  'activity_entry_id, activity_description, comments';

export async function getPermitsList(dsrId: string): Promise<PermitRow[]> {
  const { data, error } = await supabase
    .from('dsr_permit_entries')
    .select(PERMIT_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('issued_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as PermitRow[];
}

export async function savePermitRow(params: PermitInput): Promise<PermitRow> {
  const userId = await getAuthUserId();
  const verifiedAt = params.isVerified ? new Date().toISOString() : null;
  const verifiedBy = params.isVerified ? userId : null;
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    permit_type: params.permitType,
    permit_number: params.permitNumber,
    status: params.status,
    issued_by: params.issuedBy,
    issued_at: params.issuedAt,
    expires_at: params.expiresAt ?? null,
    work_location: params.workLocation ?? null,
    is_verified: params.isVerified,
    verified_by: verifiedBy,
    verified_at: verifiedAt,
    activity_entry_id: params.activityEntryId ?? null,
    activity_description: params.activityDescription ?? null,
    comments: params.comments ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_permit_entries')
      .update(payload)
      .eq('id', params.id)
      .select(PERMIT_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as PermitRow;
  }
  const { data, error } = await supabase
    .from('dsr_permit_entries')
    .insert(payload)
    .select(PERMIT_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as PermitRow;
}

export async function deletePermitRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_permit_entries')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Safety Meetings ─────────────────────────────────────────────────────────

export type MeetingType =
  | 'toolbox_talk'
  | 'pre_job_safety_meeting'
  | 'jsa_review'
  | 'incident_review'
  | 'emergency_drill_briefing'
  | 'shift_change_briefing'
  | 'other';

export interface MeetingRow {
  id: string;
  dsr_report_id: string;
  meeting_type: MeetingType;
  topic: string;
  meeting_time: string;
  duration_minutes: number | null;
  attendee_count: number;
  conducted_by: string;
  key_points: string | null;
  activity_entry_id: string | null;
  activity_description: string | null;
  comments: string | null;
}

export interface MeetingInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  meetingType: MeetingType;
  topic: string;
  meetingTime: string;
  durationMinutes?: number | null;
  attendeeCount: number;
  conductedBy: string;
  keyPoints?: string | null;
  activityEntryId?: string | null;
  activityDescription?: string | null;
  comments?: string | null;
}

const MEETING_SELECT =
  'id, dsr_report_id, meeting_type, topic, meeting_time, duration_minutes, ' +
  'attendee_count, conducted_by, key_points, activity_entry_id, ' +
  'activity_description, comments';

export async function getMeetingsList(dsrId: string): Promise<MeetingRow[]> {
  const { data, error } = await supabase
    .from('dsr_meeting_entries')
    .select(MEETING_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('meeting_time', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as MeetingRow[];
}

export async function saveMeetingRow(params: MeetingInput): Promise<MeetingRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    meeting_type: params.meetingType,
    topic: params.topic,
    meeting_time: params.meetingTime,
    duration_minutes: params.durationMinutes ?? null,
    attendee_count: params.attendeeCount,
    conducted_by: params.conductedBy,
    key_points: params.keyPoints ?? null,
    activity_entry_id: params.activityEntryId ?? null,
    activity_description: params.activityDescription ?? null,
    comments: params.comments ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_meeting_entries')
      .update(payload)
      .eq('id', params.id)
      .select(MEETING_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as MeetingRow;
  }
  const { data, error } = await supabase
    .from('dsr_meeting_entries')
    .insert(payload)
    .select(MEETING_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as MeetingRow;
}

export async function deleteMeetingRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_meeting_entries')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── JSA Evaluations ─────────────────────────────────────────────────────────

export type JSAAdequacyRating = 'adequate' | 'adequate_with_comments' | 'inadequate';
export type JSAProcessAdherence = 'following_jsa' | 'minor_deviations' | 'not_following_jsa';

export interface JSAEvaluationRow {
  id: string;
  dsr_report_id: string;
  jsa_reference: string;
  activity_entry_id: string | null;
  activity_description: string | null;
  work_crew: string | null;
  adequacy_rating: JSAAdequacyRating;
  hazards_identified_count: number | null;
  controls_in_place: string | null;
  process_adherence: JSAProcessAdherence;
  corrective_action: string | null;
  observer_name: string | null;
  comments: string | null;
}

export interface JSAEvaluationInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  jsaReference: string;
  activityEntryId?: string | null;
  activityDescription?: string | null;
  workCrew?: string | null;
  adequacyRating: JSAAdequacyRating;
  hazardsIdentifiedCount?: number | null;
  controlsInPlace?: string | null;
  processAdherence: JSAProcessAdherence;
  correctiveAction?: string | null;
  observerName?: string | null;
  comments?: string | null;
}

const JSA_SELECT =
  'id, dsr_report_id, jsa_reference, activity_entry_id, activity_description, ' +
  'work_crew, adequacy_rating, hazards_identified_count, controls_in_place, ' +
  'process_adherence, corrective_action, observer_name, comments';

export async function getJSAEvaluationsList(dsrId: string): Promise<JSAEvaluationRow[]> {
  const { data, error } = await supabase
    .from('dsr_jsa_evaluations')
    .select(JSA_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as JSAEvaluationRow[];
}

export async function saveJSAEvaluationRow(
  params: JSAEvaluationInput,
): Promise<JSAEvaluationRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    jsa_reference: params.jsaReference,
    activity_entry_id: params.activityEntryId ?? null,
    activity_description: params.activityDescription ?? null,
    work_crew: params.workCrew ?? null,
    adequacy_rating: params.adequacyRating,
    hazards_identified_count: params.hazardsIdentifiedCount ?? null,
    controls_in_place: params.controlsInPlace ?? null,
    process_adherence: params.processAdherence,
    corrective_action: params.correctiveAction ?? null,
    observer_name: params.observerName ?? null,
    comments: params.comments ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_jsa_evaluations')
      .update(payload)
      .eq('id', params.id)
      .select(JSA_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as JSAEvaluationRow;
  }
  const { data, error } = await supabase
    .from('dsr_jsa_evaluations')
    .insert(payload)
    .select(JSA_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as JSAEvaluationRow;
}

export async function deleteJSAEvaluationRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_jsa_evaluations')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── SWA Events ──────────────────────────────────────────────────────────────

export type SWASeverity = 'minor' | 'moderate' | 'serious' | 'critical';
export type SWAWorkStatus = 'stopped' | 'resumed' | 'ongoing';

export interface SWAEventRow {
  id: string;
  dsr_report_id: string;
  invoked_at: string;
  hazard_description: string;
  action_taken: string;
  invoked_by: string;
  outcome: string | null;
  site_location: string;
  activity_description: string;
  activity_entry_id: string | null;
  work_crew: string | null;
  severity: SWASeverity;
  category: string[];
  personnel_count: number | null;
  is_supervisor_notified: boolean;
  supervisor_name: string | null;
  is_management_notified: boolean;
  work_status: SWAWorkStatus;
  authorized_resumption_by: string | null;
  resumption_time: string | null;
  lessons_learned: string | null;
  jsa_evaluation_id: string | null;
}

export interface SWAEventInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  invokedAt: string;
  hazardDescription: string;
  actionTaken: string;
  invokedBy: string;
  outcome?: string | null;
  siteLocation: string;
  activityDescription: string;
  activityEntryId?: string | null;
  workCrew?: string | null;
  severity: SWASeverity;
  category: string[];
  personnelCount?: number | null;
  isSupervisorNotified: boolean;
  supervisorName?: string | null;
  isManagementNotified: boolean;
  workStatus: SWAWorkStatus;
  authorizedResumptionBy?: string | null;
  resumptionTime?: string | null;
  lessonsLearned?: string | null;
  jsaEvaluationId?: string | null;
}

const SWA_SELECT =
  'id, dsr_report_id, invoked_at, hazard_description, action_taken, invoked_by, ' +
  'outcome, site_location, activity_description, activity_entry_id, work_crew, ' +
  'severity, category, personnel_count, is_supervisor_notified, supervisor_name, ' +
  'is_management_notified, work_status, authorized_resumption_by, resumption_time, ' +
  'lessons_learned, jsa_evaluation_id';

export async function getSWAEventsList(dsrId: string): Promise<SWAEventRow[]> {
  const { data, error } = await supabase
    .from('dsr_swa_events')
    .select(SWA_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('invoked_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SWAEventRow[];
}

export async function saveSWAEventRow(params: SWAEventInput): Promise<SWAEventRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    invoked_at: params.invokedAt,
    hazard_description: params.hazardDescription,
    action_taken: params.actionTaken,
    invoked_by: params.invokedBy,
    outcome: params.outcome ?? null,
    site_location: params.siteLocation,
    activity_description: params.activityDescription,
    activity_entry_id: params.activityEntryId ?? null,
    work_crew: params.workCrew ?? null,
    severity: params.severity,
    category: params.category,
    personnel_count: params.personnelCount ?? null,
    is_supervisor_notified: params.isSupervisorNotified,
    supervisor_name: params.supervisorName ?? null,
    is_management_notified: params.isManagementNotified,
    work_status: params.workStatus,
    authorized_resumption_by: params.authorizedResumptionBy ?? null,
    resumption_time: params.resumptionTime ?? null,
    lessons_learned: params.lessonsLearned ?? null,
    jsa_evaluation_id: params.jsaEvaluationId ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_swa_events')
      .update(payload)
      .eq('id', params.id)
      .select(SWA_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as SWAEventRow;
  }
  const { data, error } = await supabase
    .from('dsr_swa_events')
    .insert(payload)
    .select(SWA_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as SWAEventRow;
}

export async function deleteSWAEventRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_swa_events')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Safety Events ───────────────────────────────────────────────────────────

export type SafetyEventType =
  | 'near_miss'
  | 'first_aid'
  | 'recordable_injury'
  | 'lost_time_injury'
  | 'property_damage'
  | 'environmental_release'
  | 'security_incident';

export type PotentialSeverity =
  | 'minor'
  | 'moderate'
  | 'serious'
  | 'critical'
  | 'catastrophic';

export interface SafetyEventRow {
  id: string;
  dsr_report_id: string;
  event_type: SafetyEventType;
  event_datetime: string;
  location_area: string;
  event_title: string;
  event_description: string;
  immediate_actions: string;
  category: string[];
  is_work_stopped: boolean;
  personnel_count: number;
  activity_entry_id: string | null;
  activity_description: string | null;
  swa_event_id: string | null;
  permit_entry_id: string | null;
  jsa_evaluation_id: string | null;
  contractor_crew: string | null;
  is_supervisor_notified: boolean;
  is_management_notified: boolean;
  is_regulatory_required: boolean;
  near_miss_potential_severity: PotentialSeverity | null;
  near_miss_conditions: string | null;
  injured_person_role: string | null;
  injury_type: string | null;
  body_part_affected: string | null;
  treatment_provided: string | null;
}

export interface SafetyEventInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  eventType: SafetyEventType;
  eventDatetime: string;
  locationArea: string;
  eventTitle: string;
  eventDescription: string;
  immediateActions: string;
  category: string[];
  isWorkStopped: boolean;
  personnelCount: number;
  activityEntryId?: string | null;
  activityDescription?: string | null;
  swaEventId?: string | null;
  permitEntryId?: string | null;
  jsaEvaluationId?: string | null;
  contractorCrew?: string | null;
  isSupervisorNotified: boolean;
  isManagementNotified: boolean;
  isRegulatoryRequired: boolean;
  nearMissPotentialSeverity?: PotentialSeverity | null;
  nearMissConditions?: string | null;
  injuredPersonRole?: string | null;
  injuryType?: string | null;
  bodyPartAffected?: string | null;
  treatmentProvided?: string | null;
}

const SAFETY_EVENT_SELECT =
  'id, dsr_report_id, event_type, event_datetime, location_area, event_title, ' +
  'event_description, immediate_actions, category, is_work_stopped, personnel_count, ' +
  'activity_entry_id, activity_description, swa_event_id, permit_entry_id, ' +
  'jsa_evaluation_id, contractor_crew, is_supervisor_notified, is_management_notified, ' +
  'is_regulatory_required, near_miss_potential_severity, near_miss_conditions, ' +
  'injured_person_role, injury_type, body_part_affected, treatment_provided';

export async function getSafetyEventsList(dsrId: string): Promise<SafetyEventRow[]> {
  const { data, error } = await supabase
    .from('dsr_safety_events')
    .select(SAFETY_EVENT_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('event_datetime', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SafetyEventRow[];
}

export async function saveSafetyEventRow(
  params: SafetyEventInput,
): Promise<SafetyEventRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    event_type: params.eventType,
    event_datetime: params.eventDatetime,
    location_area: params.locationArea,
    event_title: params.eventTitle,
    event_description: params.eventDescription,
    immediate_actions: params.immediateActions,
    category: params.category,
    is_work_stopped: params.isWorkStopped,
    personnel_count: params.personnelCount,
    activity_entry_id: params.activityEntryId ?? null,
    activity_description: params.activityDescription ?? null,
    swa_event_id: params.swaEventId ?? null,
    permit_entry_id: params.permitEntryId ?? null,
    jsa_evaluation_id: params.jsaEvaluationId ?? null,
    contractor_crew: params.contractorCrew ?? null,
    is_supervisor_notified: params.isSupervisorNotified,
    is_management_notified: params.isManagementNotified,
    is_regulatory_required: params.isRegulatoryRequired,
    near_miss_potential_severity: params.nearMissPotentialSeverity ?? null,
    near_miss_conditions: params.nearMissConditions ?? null,
    injured_person_role: params.injuredPersonRole ?? null,
    injury_type: params.injuryType ?? null,
    body_part_affected: params.bodyPartAffected ?? null,
    treatment_provided: params.treatmentProvided ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_safety_events')
      .update(payload)
      .eq('id', params.id)
      .select(SAFETY_EVENT_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as SafetyEventRow;
  }
  const { data, error } = await supabase
    .from('dsr_safety_events')
    .insert(payload)
    .select(SAFETY_EVENT_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as SafetyEventRow;
}

export async function deleteSafetyEventRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_safety_events')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── HSE Observations (Good Catch) ───────────────────────────────────────────

export interface HSEObservationRow {
  id: string;
  dsr_report_id: string;
  observation_type: 'positive' | 'at_risk' | 'pta';
  description: string;
  observation_category: string;
  location_detail: string | null;
  associated_crew: string | null;
  activity_entry_id: string | null;
  activity_free_text: string | null;
  observation_time: string | null;
  comments: string | null;
}

export interface HSEObservationInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  description: string;
  observationCategory: string;
  locationDetail?: string | null;
  associatedCrew?: string | null;
  activityEntryId?: string | null;
  activityFreeText?: string | null;
  observationTime?: string | null;
  comments?: string | null;
}

const HSE_OBSERVATION_SELECT =
  'id, dsr_report_id, observation_type, description, observation_category, ' +
  'location_detail, associated_crew, activity_entry_id, activity_free_text, ' +
  'observation_time, comments';

export async function getHSEObservationsList(dsrId: string): Promise<HSEObservationRow[]> {
  const { data, error } = await supabase
    .from('dsr_hse_observations')
    .select(HSE_OBSERVATION_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as HSEObservationRow[];
}

export async function saveHSEObservationRow(
  params: HSEObservationInput,
): Promise<HSEObservationRow> {
  const userId = await getAuthUserId();
  // Why: web Good Catch form only writes 'positive' observations. The 'at_risk'
  // and 'pta' types are reserved for future tabs/sections.
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    observation_type: 'positive' as const,
    description: params.description,
    observation_category: params.observationCategory,
    location_detail: params.locationDetail ?? null,
    associated_crew: params.associatedCrew ?? null,
    activity_entry_id: params.activityEntryId ?? null,
    activity_free_text: params.activityFreeText ?? null,
    observation_time: params.observationTime ?? null,
    comments: params.comments ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_hse_observations')
      .update(payload)
      .eq('id', params.id)
      .select(HSE_OBSERVATION_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as HSEObservationRow;
  }
  const { data, error } = await supabase
    .from('dsr_hse_observations')
    .insert(payload)
    .select(HSE_OBSERVATION_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as HSEObservationRow;
}

export async function deleteHSEObservationRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_hse_observations')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Forecasting (Forecasted Activities + Outstanding Items + Summary) ───────

export type ForecastRiskLevel = 'high' | 'medium' | 'low';
export type ItemPriority = 'high' | 'medium' | 'low';
export type NextShiftRiskAssessment =
  | 'low_risk'
  | 'elevated_risk'
  | 'high_risk'
  | 'critical_risk';
export type ResourceAdequacy =
  | 'fully_adequate'
  | 'minor_gaps'
  | 'significant_gaps'
  | 'inadequate';

export interface ForecastedActivityRow {
  id: string;
  dsr_report_id: string;
  description: string;
  anticipated_risk: ForecastRiskLevel;
  risk_notes: string | null;
  activity_tags: string[] | null;
  is_permits_required: boolean;
  estimated_duration: number | null;
  notes: string | null;
}

export interface ForecastedActivityInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  description: string;
  anticipatedRisk: ForecastRiskLevel;
  riskNotes?: string | null;
  activityTags?: string[] | null;
  isPermitsRequired: boolean;
  estimatedDuration?: number | null;
  notes?: string | null;
}

const FORECASTED_ACTIVITY_SELECT =
  'id, dsr_report_id, description, anticipated_risk, risk_notes, activity_tags, ' +
  'is_permits_required, estimated_duration, notes';

export async function getForecastedActivitiesList(
  dsrId: string,
): Promise<ForecastedActivityRow[]> {
  const { data, error } = await supabase
    .from('dsr_forecasted_activities')
    .select(FORECASTED_ACTIVITY_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ForecastedActivityRow[];
}

export async function saveForecastedActivityRow(
  params: ForecastedActivityInput,
): Promise<ForecastedActivityRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    description: params.description,
    anticipated_risk: params.anticipatedRisk,
    risk_notes: params.riskNotes ?? null,
    activity_tags: params.activityTags ?? null,
    is_permits_required: params.isPermitsRequired,
    estimated_duration: params.estimatedDuration ?? null,
    notes: params.notes ?? null,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_forecasted_activities')
      .update(payload)
      .eq('id', params.id)
      .select(FORECASTED_ACTIVITY_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as ForecastedActivityRow;
  }
  const { data, error } = await supabase
    .from('dsr_forecasted_activities')
    .insert(payload)
    .select(FORECASTED_ACTIVITY_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as ForecastedActivityRow;
}

export async function deleteForecastedActivityRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_forecasted_activities')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export interface OutstandingItemRow {
  id: string;
  dsr_report_id: string;
  description: string;
  priority: ItemPriority;
}

export interface OutstandingItemInput {
  id?: string;
  dsrReportId: string;
  tenantId: string;
  description: string;
  priority: ItemPriority;
}

const OUTSTANDING_ITEM_SELECT = 'id, dsr_report_id, description, priority';

export async function getOutstandingItemsList(
  dsrId: string,
): Promise<OutstandingItemRow[]> {
  const { data, error } = await supabase
    .from('dsr_outstanding_items')
    .select(OUTSTANDING_ITEM_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as OutstandingItemRow[];
}

export async function saveOutstandingItemRow(
  params: OutstandingItemInput,
): Promise<OutstandingItemRow> {
  const userId = await getAuthUserId();
  const payload = {
    dsr_report_id: params.dsrReportId,
    tenant_id: params.tenantId,
    created_by: userId,
    description: params.description,
    priority: params.priority,
  };
  if (params.id) {
    const { data, error } = await supabase
      .from('dsr_outstanding_items')
      .update(payload)
      .eq('id', params.id)
      .select(OUTSTANDING_ITEM_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return data as unknown as OutstandingItemRow;
  }
  const { data, error } = await supabase
    .from('dsr_outstanding_items')
    .insert(payload)
    .select(OUTSTANDING_ITEM_SELECT)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as OutstandingItemRow;
}

export async function deleteOutstandingItemRow(id: string): Promise<void> {
  const { error } = await supabase
    .from('dsr_outstanding_items')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export interface ForecastingSummaryRow {
  id: string;
  dsr_report_id: string;
  overall_risk_assessment: NextShiftRiskAssessment;
  risk_comments: string | null;
  resource_adequacy: ResourceAdequacy;
  resource_comments: string | null;
  next_shift_narrative: string | null;
}

export interface ForecastingSummaryInput {
  dsrReportId: string;
  tenantId: string;
  overallRiskAssessment: NextShiftRiskAssessment;
  riskComments?: string | null;
  resourceAdequacy: ResourceAdequacy;
  resourceComments?: string | null;
  nextShiftNarrative?: string | null;
}

const FORECASTING_SUMMARY_SELECT =
  'id, dsr_report_id, overall_risk_assessment, risk_comments, resource_adequacy, ' +
  'resource_comments, next_shift_narrative';

export async function getForecastingSummary(
  dsrId: string,
): Promise<ForecastingSummaryRow | null> {
  const { data, error } = await supabase
    .from('dsr_forecasting_summaries')
    .select(FORECASTING_SUMMARY_SELECT)
    .eq('dsr_report_id', dsrId)
    .eq('is_deleted', false)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as ForecastingSummaryRow | null;
}

export async function saveForecastingSummary(
  params: ForecastingSummaryInput,
): Promise<void> {
  const userId = await getAuthUserId();
  const { error } = await supabase
    .from('dsr_forecasting_summaries')
    .upsert(
      {
        dsr_report_id: params.dsrReportId,
        tenant_id: params.tenantId,
        created_by: userId,
        overall_risk_assessment: params.overallRiskAssessment,
        risk_comments: params.riskComments ?? null,
        resource_adequacy: params.resourceAdequacy,
        resource_comments: params.resourceComments ?? null,
        next_shift_narrative: params.nextShiftNarrative ?? null,
      },
      { onConflict: 'dsr_report_id' },
    );
  if (error) throw new Error(error.message);
}
